import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Store, ShoppingBag } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useKiosk } from "@/components/KioskGuard";

// Vendor Market display: only the curated designs, big tiles, tap to customise.
export default function VendorMarket() {
  const navigate = useNavigate();
  const { count, subtotal } = useCart();
  const { user } = useAuth();
  const kiosk = useKiosk();
  const [q, setQ] = useState("");
  const { data: designs = [], isLoading } = useQuery({ queryKey: ["vendor-designs"], queryFn: async () => (await api.get("/vendor/designs")).data, staleTime: 30000 });
  const s = q.trim().toLowerCase().replace(/^#/, "");
  const shown = s ? designs.filter((d) => String(d.seq) === s || (d.title || "").toLowerCase().includes(s) || (d.tags || []).some((t) => t.toLowerCase().includes(s))) : designs;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vendor-market-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono-p uppercase tracking-[0.2em] text-[#ff6a13] flex items-center gap-2"><Store size={14} />Vendor Market</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-1">Pick a pre-printed design, we press it here.</h1>
          <p className="text-zinc-400 mt-2">Tap any design, choose your garment, colour, size and placement — then bring your order to the table. Cash on pickup.</p>
        </div>
        <div className="flex items-center gap-2">
          {count > 0 && <button onClick={() => navigate("/vendor/checkout")} data-testid="vendor-cart-button" className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white font-semibold"><ShoppingBag size={16} />{count} item{count === 1 ? "" : "s"} · ${subtotal.toFixed(2)} · Finish order</button>}
          {!kiosk && user?.role === "admin" && <button onClick={() => navigate("/admin?tab=vendor")} data-testid="vendor-back-admin" className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-zinc-700 text-zinc-200 hover:border-white font-semibold">Back to Admin</button>}
        </div>
      </div>
      <div className="relative mt-6 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or #number…" data-testid="vendor-search"
          className="w-full bg-[#12141a] border border-zinc-800 rounded-full h-12 pl-11 pr-4 text-white placeholder:text-zinc-500 focus:border-[#ff6a13] focus:outline-none" />
      </div>
      {isLoading ? <p className="text-zinc-500 font-mono-p mt-8">Loading designs…</p>
        : designs.length === 0 ? <p className="text-zinc-500 font-mono-p mt-8" data-testid="vendor-empty">No designs in the Vendor Market yet — add some from Admin → Catalog.</p>
        : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-6" data-testid="vendor-grid">
            {shown.map((d) => (
              <button key={d.design_id} onClick={() => navigate(`/vendor/design/${d.design_id}`)} data-testid={`vendor-design-${d.design_id}`}
                className="group text-left bg-[#12141a] border border-zinc-800 rounded-2xl overflow-hidden hover:border-[#ff6a13] active:scale-[0.98] transition-all">
                <div className="bg-white aspect-square flex items-center justify-center p-5"><img src={thumbUrl(d.image_url, 480)} alt={d.title} loading="lazy" className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform" /></div>
                <div className="p-3">
                  <p className="font-semibold text-white truncate"><span className="font-mono-p text-[#22c3d6] text-xs mr-1.5">#{d.seq}</span>{d.title}</p>
                  <p className="text-xs text-zinc-500 font-mono-p mt-0.5">design from ${Number(d.base_price || 0).toFixed(2)}</p>
                </div>
              </button>
            ))}
            {shown.length === 0 && <p className="text-zinc-500 font-mono-p col-span-full">Nothing matches “{q}”.</p>}
          </div>
        )}
    </div>
  );
}
